import { useAuthContext } from '../contexts/AuthContext'
import { useRootContext } from '../contexts/RootContext'
import React, { useEffect, useRef, useState } from 'react'
import AdSense, { AdSlots } from '../misc/AdSense'
import { removeWhiteSpace } from '../utils/utils'
import Leaderboard from "../Leaderboard";
import Error from "../Error/Error";

const archiveBracketCount = (body) => {
  const primaryCount = body?.primary_pagy?.count ?? body?.primary?.length ?? 0
  const secondChanceCount = body?.second_chance_pagy?.count ?? body?.second_chance?.length ?? 0
  return primaryCount + secondChanceCount
}

const buildArchiveDescription = (pool, usingPublicLeaderboard) => {
  if (usingPublicLeaderboard) {
    return 'Showing the public pool because the currently selected pool does not have any brackets in this archived tournament.'
  }

  if (pool?.name) {
    return `Showing archived brackets from the ${pool.name} pool.`
  }

  return 'Showing archived brackets from the public pool.'
}

export default function Archive({ tournament }) {
  const [loading, setLoading] = useState(true)
  const [brackets, setBrackets] = useState([])
  const [pagy, setPagy] = useState({})
  const [secondChance, setSecondChance] = useState([])
  const [secondChancePagy, setSecondChancePagy] = useState({})
  const [activePoolId, setActivePoolId] = useState(null)
  const [usingPublicLeaderboard, setUsingPublicLeaderboard] = useState(false)
  const [error, setError] = useState(null)
  const requestIdRef = useRef(0)

  const { authorizationHeaders } = useAuthContext()
  const { pool } = useRootContext()

  const fetchLeaderboard = (secondChance, search = '', page = 1, pageSize = 100, sort = 'rank', order = 'ascend', poolId = activePoolId) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    setLoading(true)

    const poolQuery = poolId ? `&pool=${poolId}` : ''

    fetch(encodeURI(`/api/v1/leaderboard/${tournament}?archive=true&page=${page}&pageSize=${pageSize}&sort=${sort}&order=${order}&search=${search}&secondChance=${secondChance}${poolQuery}`), {
      credentials: 'same-origin',
      headers: authorizationHeaders,
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        throw(response);
      }
    })
    .then(body => {
      if (requestId !== requestIdRef.current) return
      setBrackets(body.primary)
      setPagy(body.primary_pagy)
      setSecondChance(body.second_chance)
      setSecondChancePagy(body.second_chance_pagy)
      setLoading(false)
    })
    .catch(() => {
      if (requestId !== requestIdRef.current) return
      setError({ status: 503 })
      setLoading(false)
    })
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    setBrackets([])
    setPagy({})
    setSecondChance([])
    setSecondChancePagy({})
    setActivePoolId(null)
    setUsingPublicLeaderboard(false)

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    const applyBody = (body, poolIdUsed) => {
      if (requestId !== requestIdRef.current) return
      setBrackets(body.primary)
      setPagy(body.primary_pagy)
      setSecondChance(body.second_chance)
      setSecondChancePagy(body.second_chance_pagy)
      setActivePoolId(poolIdUsed)
      setUsingPublicLeaderboard(!poolIdUsed)
      setLoading(false)
    }

    const handleError = () => {
      if (requestId !== requestIdRef.current) return
      setError({ status: 503 })
      setLoading(false)
    }

    const fetchArchive = (poolId) => {
      const poolQuery = poolId ? `&pool=${poolId}` : ''

      return fetch(encodeURI(`/api/v1/leaderboard/${tournament}?archive=true&page=1&pageSize=100&sort=rank&order=ascend&search=&secondChance=false${poolQuery}`), {
        credentials: 'same-origin',
        headers: authorizationHeaders,
      })
        .then(response => {
          if (response.ok) {
            return response.json()
          }
          throw response
        })
    }

    if (pool?.id) {
      fetchArchive(pool.id)
        .then(body => {
          if (archiveBracketCount(body) > 0) {
            applyBody(body, pool.id)
            return
          }

          return fetchArchive(null).then(publicBody => applyBody(publicBody, null))
        })
        .catch(handleError)
    } else {
      fetchArchive(null)
        .then(body => applyBody(body, null))
        .catch(handleError)
    }
  }, [tournament, authorizationHeaders, pool?.id])

  if (error) {
    return <Error error={error} />
  }

  return (
    <div className={removeWhiteSpace(tournament)}>
      <div className="background-image leaderboard-background"/>
      <AdSense slot={AdSlots.LeaderboardHeader} />
      <div className="leaderboard-container">
        <div className="header-container white-box-shadow">
        <h1 className="leaderboard wc-font wc-primary">{`${tournament} Leaderboard`}</h1>
        <div className="header-description wc-font">
          <p>{buildArchiveDescription(pool, usingPublicLeaderboard)}</p>
        </div>
      </div>
        <div className="double-leaderboard">
          <Leaderboard archive fetchArchiveLeaderboard={fetchLeaderboard} loading={loading} name="Brackets" brackets={brackets} pagy={pagy}/>
          <Leaderboard archive fetchArchiveLeaderboard={fetchLeaderboard} loading={loading} name="Second Chance" brackets={secondChance} pagy={secondChancePagy}/>
        </div>
      </div>
      <AdSense slot={AdSlots.LeaderboardFooter1} />
      <AdSense slot={AdSlots.LeaderboardFooter2} />
      <AdSense slot={AdSlots.LeaderboardFooter3} />
    </div>
  )
}
